import {
  getEuAlarmTypeLabel,
  getSiteAddressLine,
  getSiteDisplayName,
  getMeta,
  getAlarmPressCount,
  type PendingButtonAlertRow,
} from './pendingButtonAlertRow';
import {
  notificationPayloadToNum,
  pickMapNotificationId,
  type NotificationPayload,
} from './notificationPayload';
import { resolveEuAlarmTypeLabel } from './euEscudoUrbanoDisplay';

/** Alerta en el panel del mapa (socket y/o API pendientes). */
export interface NotificationItem {
  id: number;
  receivedAt?: number;
  buttonAlertId?: number;
  deviceId?: number;
  sourceType?: string;
  type?: string;
  message?: string;
  scope?: string;
  latitude?: number;
  longitude?: number;
  siteName?: string;
  siteId?: number;
  sound?: number;
  alertType?: string;
  /** Igual que campana / toast global */
  siteAddress?: string;
  siteDisplayName?: string;
  alarmLabelDisplay?: string;
  timestampIso?: string;
  /** Pulsaciones agrupadas (mismo sitio + tipo) */
  pressCount?: number;
  /** Notificaciones de workers (scene-monitor, system-state) */
  mapNotificationId?: number;
  mapNotificationKind?: string;
  /** Título corto (p. ej. análisis de escena) */
  notificationTitle?: string;
  [key: string]: unknown;
}

function metadataRecord(flat: Record<string, unknown>): Record<string, unknown> | undefined {
  const m = flat.metadata;
  if (m && typeof m === 'object' && !Array.isArray(m)) return m as Record<string, unknown>;
  return undefined;
}

export function pendingRowToNotificationItem(a: PendingButtonAlertRow): NotificationItem {
  const meta = getMeta(a);
  const lat = meta.latitud != null ? Number(meta.latitud) : NaN;
  const lng = meta.longitud != null ? Number(meta.longitud) : NaN;
  const ts = a.createdAt;
  const receivedAt = ts && !Number.isNaN(Date.parse(ts)) ? Date.parse(ts) : Date.now();
  const timestampIso =
    ts && ts.trim() !== '' && !Number.isNaN(Date.parse(ts)) ? ts : new Date(receivedAt).toISOString();

  return {
    id: a.id,
    buttonAlertId: a.id,
    siteId: a.siteId ?? undefined,
    deviceId: a.deviceId,
    siteName: meta.site_name != null ? String(meta.site_name) : undefined,
    message: a.message,
    alertType: a.alertType,
    latitude: Number.isFinite(lat) ? lat : undefined,
    longitude: Number.isFinite(lng) ? lng : undefined,
    sound: meta.sound != null && meta.sound !== '' ? Number(meta.sound) : undefined,
    scope: 'map',
    receivedAt,
    timestampIso,
    siteDisplayName: getSiteDisplayName(a),
    siteAddress: getSiteAddressLine(a),
    alarmLabelDisplay: getEuAlarmTypeLabel(a),
    pressCount: getAlarmPressCount(a),
  };
}

export type MapNotificationPendingRow = {
  id: number;
  siteId: number;
  origin: string;
  comment: string;
  isShown?: boolean;
  isAccepted?: boolean;
  createdAt: string;
  siteName?: string;
  siteAddress?: string;
};

/**
 * Notificaciones que siguen en BD / tabla ``notifications`` pero no deben listarse en el panel
 * lateral del mapa (ruido operativo: system-state; escenas desde scene-monitor worker).
 */
export function showInMapSidePanel(item: NotificationItem): boolean {
  const kind = String(item.mapNotificationKind ?? item.alertType ?? '')
    .trim()
    .toLowerCase();
  if (kind === 'system_state') return false;
  if (kind === 'scene_monitor') return false;
  return true;
}

export function mapNotificationRowToItem(r: MapNotificationPendingRow): NotificationItem {
  const ts = r.createdAt;
  const receivedAt = ts && !Number.isNaN(Date.parse(ts)) ? Date.parse(ts) : Date.now();
  const timestampIso =
    ts && ts.trim() !== '' && !Number.isNaN(Date.parse(ts)) ? new Date(ts).toISOString() : new Date(receivedAt).toISOString();
  const origin = r.origin;
  return {
    id: r.id,
    mapNotificationId: r.id,
    mapNotificationKind: origin,
    siteId: r.siteId,
    siteName: r.siteName,
    siteDisplayName: r.siteName ?? (r.siteId != null ? `Sitio #${r.siteId}` : undefined),
    siteAddress: r.siteAddress,
    message: r.comment,
    notificationTitle:
      origin === 'scene_monitor'
        ? 'Escena'
        : origin === 'system_state'
          ? 'Estado del sitio'
          : 'Aviso',
    receivedAt,
    timestampIso,
    scope: 'map',
    sourceType: origin === 'scene_monitor' ? 'scene' : origin === 'system_state' ? 'state' : 'app',
  };
}

export function enrichNotificationItemFromSocket(
  flat: Record<string, unknown>,
  norm: NotificationPayload | null,
  base: NotificationItem
): NotificationItem {
  const siteId = norm?.siteId ?? notificationPayloadToNum(flat.siteId);
  const siteName =
    typeof flat.siteName === 'string' ? flat.siteName : norm?.siteName;
  const message = typeof flat.message === 'string' ? flat.message : norm?.message;
  const meta = metadataRecord(flat);
  const fromPayload =
    typeof flat.siteAddress === 'string' && flat.siteAddress.trim() !== ''
      ? flat.siteAddress.trim()
      : (norm?.siteAddress?.trim() ?? '');
  const siteAddress =
    fromPayload ||
    (meta?.site_address != null && String(meta.site_address).trim() !== ''
      ? String(meta.site_address).trim()
      : '') ||
    '—';
  const aidRaw = meta?.alarm_type_id;
  const alarmTypeId = aidRaw != null && aidRaw !== '' ? Number(aidRaw) : undefined;
  const soundNum = norm?.sound ?? notificationPayloadToNum(flat.sound);
  const alarmLabelDisplay = resolveEuAlarmTypeLabel({
    alarmTypeId: alarmTypeId != null && Number.isFinite(alarmTypeId) ? alarmTypeId : undefined,
    sound: soundNum,
    message,
    alertType: typeof flat.alertType === 'string' ? flat.alertType : norm?.alertType,
  });
  const siteDisplayName =
    (typeof siteName === 'string' && siteName.trim() !== ''
      ? siteName.trim()
      : null) || (siteId != null ? `Sitio #${siteId}` : 'Sin nombre de sitio');
  const timestampIso =
    norm?.timestamp && norm.timestamp.trim() !== '' && !Number.isNaN(Date.parse(norm.timestamp))
      ? norm.timestamp
      : new Date().toISOString();

  const pressFromNorm = norm?.pressCount;
  const pressFromFlat = notificationPayloadToNum(flat.pressCount ?? flat.press_count);
  const pressFromMeta =
    meta?.press_count != null && String(meta.press_count).trim() !== ''
      ? Number(meta.press_count)
      : undefined;
  const pressRaw =
    pressFromNorm ??
    (pressFromFlat != null && pressFromFlat >= 1 ? pressFromFlat : undefined) ??
    (pressFromMeta != null && Number.isFinite(pressFromMeta) && pressFromMeta >= 1
      ? Math.floor(pressFromMeta)
      : undefined);
  const pressCount =
    pressRaw != null && pressRaw >= 1 ? Math.min(9999, Math.floor(pressRaw)) : base.pressCount;

  const mapNotificationId = norm?.mapNotificationId ?? pickMapNotificationId(flat);
  const mapKindRaw = norm?.mapNotificationKind ?? (typeof flat.mapNotificationKind === 'string' ? flat.mapNotificationKind : undefined);
  const mapNotificationKind =
    mapKindRaw != null && String(mapKindRaw).trim() !== ''
      ? String(mapKindRaw).trim()
      : base.mapNotificationKind;

  let notificationTitle = base.notificationTitle;
  const mid = mapNotificationId ?? base.mapNotificationId;
  const mk = mapNotificationKind ?? base.mapNotificationKind;
  if (mid != null && mk && !notificationTitle) {
    notificationTitle =
      mk === 'scene_monitor' ? 'Escena' : mk === 'system_state' ? 'Estado del sitio' : 'Aviso';
  }

  return {
    ...base,
    siteAddress,
    siteDisplayName,
    alarmLabelDisplay,
    timestampIso,
    ...(pressCount != null ? { pressCount } : {}),
    ...(mapNotificationId != null ? { mapNotificationId } : {}),
    ...(mapNotificationKind ? { mapNotificationKind } : {}),
    ...(notificationTitle ? { notificationTitle } : {}),
  };
}
